<script setup lang="ts">
import { computed, onMounted, defineProps } from 'vue';

import { useRulesStore } from '@/store/rules';
import { useEstimatedSegments } from '@/composables/useEstimatedSegments';
import type { RuleOptionItem } from '@/utils/types';

interface Props {
  item: RuleOptionItem;
  modelValue?: number | null;
}

const props = defineProps<Props>();

const { getRuleById, fetch } = useRulesStore();
const { segmentRules } = useEstimatedSegments({
  visibility: 'all'
});

const rule = computed(() => {
  return getRuleById(props.item.rule_id);
});

const ruleEvaluation = computed(() => {
  if (!rule.value || !segmentRules.value) return null;

  for (const segment of segmentRules.value) {
    const segmentRule = segment.rules.find((r) => r.id === rule.value?.id);
    if (segmentRule) {
      return {
        eval: segmentRule.eval,
        expression: segmentRule.expression,
      };
    }

    for (const group of segment.groups) {
      const groupRule = group.rules.find((r) => r.id === rule.value?.id);
      if (groupRule) {
        return {
          eval: groupRule.eval,
          expression: groupRule.expression,
        };
      }
    }
  }

  return null;
});

const displayValue = computed(() => {
  if (!ruleEvaluation.value) return 'Brak danych';

  return ruleEvaluation.value.eval;
});

onMounted(async () => {
  await fetch();
});
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h3 class="text-sm font-medium text-gray-900">{{ item.name }}</h3>
      </div>
    </div>

    <div class="rounded-lg border bg-gray-50 p-4">
      <div class="flex items-center">
        <div class="flex gap-1 pl-1">
          <div class="text-sm font-semibold text-gray-900">{{ displayValue }}</div>
          <div v-if="rule?.unit" class="text-sm text-gray-600">{{ rule.unit }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
